<script lang="ts">
  import 'tippy.js/animations/shift-away-extreme.css';
  import 'tippy.js/dist/border.css';
  import 'tippy.js/dist/tippy.css';
  import 'tippy.js/themes/light.css';

  import {onMount} from 'svelte';

  import appConfig from '@/config';
  import {appEvents, UserService} from '@/services';
  import {credit, featuresStore, isMobile, metadata, modal, renderer, step, user} from '@/store';
  import {Content, Head} from '@components';
  import Modal from 'svelte-simple-modal';
  import {FlatToast, ToastContainer} from 'svelte-toasts';
  import Swal from 'sweetalert2';
  import NavigationBar from './components/navigationbar/NavigationBar.svelte';
  import NewSideBar from './components/navigationbar/NewSideBar.svelte';
  import EditorStyle from './lib/editor/EditorStyle.svelte';
  import VideoInformationDialog from './pages/choose-video-redesign/Dialogs/VideoInformationDialog.svelte';
  import Renderer from './pages/renderer/Renderer.svelte';
  import {getInstance} from './services/animation';
  import {STEP} from './types/common';
  import {mobileAndTabletCheck} from './utils/common';
  import {events} from './utils/custom-event-names';
  import {enableInterceptor} from './utils/http-client';

  // Check URL params for project loading
  const urlSearchParams = new URLSearchParams(window.location.search);
  let projectId: number | null = urlSearchParams.has('project_id')
    ? parseInt(urlSearchParams.get('project_id')!)
    : null;
  let loadProject: boolean =
    urlSearchParams.has('load_project') || (projectId !== null && !window.location.href.includes('clipper-render'));

  $: containerClassVar =
    $step === STEP.CHOOSE_VIDEO ? 'h-[calc(100%)] w-full flex' : 'h-full w-full flex overflow-none';

  isMobile.set(mobileAndTabletCheck());
  let userInfo: any | null = {info: {}};
  onMount(async () => {
    try {
      featuresStore.init();
    } catch (err) {
      console.error('Error initializing features store:', err);
    }
    const inMaintenance = appConfig.inMaintenance;
    const appHeight = () => document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    window.addEventListener('resize', appHeight);
    appHeight();

    // Check if trying to access settings page from the Menu
    const urlParams = new URLSearchParams(window.location.search);
    const isSettings = urlParams.has('settings');
    const page = urlParams.get('page');
    const isAdmin = window.uData && uData.is_admin;
    const isImpersonated = window.uData && uData.is_switched;

    if (isSettings && isAdmin) {
      step.update(STEP.SETTINGS);
    } else if (page) {
      console.log('Loading page: ', page);
      switch (page) {
        case 'brandkit':
          step.update(STEP.BRANDKIT);
          break;
        default:
          step.update(STEP.CHOOSE_VIDEO);
          console.log('Loading default page.');
          break;
      }
    }

    try {
      const avatarURL = (window as any).avatarURL || '';

      if (uData) {
        appEvents.subscribe(uData.id);
        const userService = new UserService();

        userService.updatePersonalInfo(uData.user_firstname, uData.user_lastname, avatarURL);
        user.getUser(isAdmin, isImpersonated).then(async response => {
          await credit.setCredits(response.usage);
        });

        if (inMaintenance == true && uData.is_admin) {
          console.log('Under Maintenance - Admin Access');
        } else if (inMaintenance) {
          console.log('Under Maintenance');
          step.update(STEP.MAINTENANCE);
        }
      }
    } catch (err) {
      console.log('Unable to Subscribe to SSE');
    }

    let ua = navigator.userAgent.toLowerCase();
    if (ua.indexOf('safari') != -1) {
      if (ua.indexOf('chrome') > -1) {
        console.log('[Chrome user detected]');
      } else if (navigator.userAgent.match('CriOS') || navigator.userAgent.match('crios')) {
        // Chrome on iOS
        console.log('[Chrome iOS user detected]');
      } else {
        console.warn('[Safari user detected]');
        Swal.fire(
          'Safari Browser Notice',
          'We have detected that you are using Safari. You can continue using it, but for the best experience with Sermon Shots, especially for video editing, we recommend using <a class="text-blue-800 underline" href="https://www.google.com/chrome">Google Chrome</a>. Chrome offers better performance and a smoother experience for our video editing features.',
          'warning'
        );
      }
    }

    const setupMetaData = async () => {
      metadata.getMusic();
      metadata.getFonts();
      metadata.getTextAnimations();
    };
    // check if "clipper-renderer" is in the url
    const isRenderer = window.location.href.includes('clipper-render');
    console.log('isrenderer', isRenderer, projectId);
    if (!projectId) {
      // check if there is a get param for ?project_id=364518
      const urlParams = new URLSearchParams(window.location.search);
      const projectIdParam = urlParams.get('project_id');
      if (projectIdParam) {
        projectId = parseInt(projectIdParam);
        console.log('projectIdParam', projectId);
      }
    }
    if ((isRenderer || loadProject) && projectId) {
      console.log('[RENDERER] LOADING PROJECT ID', projectId);
      renderer.setProject(projectId);
      // step.update(STEP.RENDER);
    } else {
      enableInterceptor();
      user.getBrandKit();
      setupMetaData();
    }
    const animService = getInstance();

    user.subscribe(store => {
      animService.setMetaData({brandKit: store.brandKit}, true);
      userInfo.info = {
        firstName: store?.details?.firstName,
        lastName: store?.details?.lastName,
        email: store?.details?.email,
        avatarLink: store?.details?.avatarLink
      };
    });
  });

  let openSidebar = false;
  let isStepOne = false;

  isMobile.subscribe(val => {
    if (val) openSidebar = false;
  });

  $: if (loadProject) openSidebar = false;
  $: isStepOne = $step == STEP.CHOOSE_VIDEO || $step == STEP.BRANDKIT;

  window.addEventListener(events.closeSideNav, () => {
    openSidebar = false;
  });

  window.addEventListener(events.openSideNav, () => {
    openSidebar = true;
  });

  const switchTheme = () => {
    if (!document.documentElement.className.includes('dark')) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
      return;
    }

    document.documentElement.classList.remove('dark');
    localStorage.removeItem('theme');
  };

  const setup = () => {
    const theme = localStorage.getItem('theme');
    const isDarkTheme = theme === 'dark';

    if (isDarkTheme) document.documentElement.classList.add('dark');

    document.addEventListener('keyup', (event: KeyboardEvent) => {
      if (event.shiftKey && event.altKey && event.code === 'KeyD') switchTheme();
    });
  };

  setup();

  let sideNavBgElement: HTMLDivElement;

  function toggleSideNav(event: Event) {
    const target = event?.target;
    console.log('toggleSideNav', {event, sideNavBgElement});
    if (target === sideNavBgElement) openSidebar = false;
  }
</script>

{#if !loadProject}
  <Head />
  <EditorStyle />
{/if}

{#if loadProject && projectId}
  <Renderer {projectId} />
{:else}
  <div class="flex h-full w-full flex-col bg-white dark:bg-zinc-800">
    <Modal
      show={$modal}
      unstyled={true}
      closeOnEsc={false}
      closeButton={false}
      closeOnOuterClick={false}
      classContent="relative overflow-auto"
      classWindowWrap="relative m-2 max-h-full"
      classWindow="relative w-full max-h-full my-2 mx-auto rounded shadow-md bg-white max-w-sm"
      classBg="fixed top-0 left-0 w-screen h-screen flex flex-col justify-center bg-black bg-opacity-20 z-50"
    >
      <VideoInformationDialog isVideoDialogOpen={false} selectedVideo={null} />

      <!-- calc(100%-5rem) is subtracting the NavigationBar height of h-20 or 5rem -->
      <div class={containerClassVar}>
        <div
          bind:this={sideNavBgElement}
          on:click={e => toggleSideNav(e)}
          on:keypress={e => toggleSideNav(e)}
          class="{openSidebar ? 'sidebar pointer-cursor absolute w-full' : ''} h-full transition-all"
          style={openSidebar ? 'background-color: rgb(0,0,0,0.4);' : ''}
        ></div>

        <div
          class="{$isMobile
            ? openSidebar
              ? 'sidebar absolute w-full'
              : 'hidden'
            : isStepOne
              ? openSidebar
                ? 'sidebar'
                : ''
              : `sidebar absolute left-0 top-0  ${openSidebar ? 'absolute' : 'hidden'}`} h-full w-[300px] bg-white transition-all"
        >
          <!-- <SideNavigationBar bind:openNavbar={openSidebar} /> -->
          {#if userInfo}
            <NewSideBar />
          {/if}
        </div>
        <div
          class="{$isMobile ? 'w-full' : isStepOne ? 'w-[calc(100%-300px)]' : 'w-full'}  h-full pb-12 transition-all"
        >
          {#if $step === STEP.HIGHLIGHT_SUBTITLE || $step === STEP.CHOOSE_TEMPLATE || $step === STEP.SELECT_SUBTITLES || $step === STEP.PREVIEW}
            <NavigationBar bind:openNavbar={openSidebar} />
          {:else if $step === STEP.CHOOSE_VIDEO}
            <div
              class="sticky flex h-20 w-full flex-row items-center space-x-4 border-b border-gray-100 bg-white p-2 px-8"
            >
              {#if $isMobile}
                <button
                  on:click={() => (openSidebar = true)}
                  class="my-3 flex aspect-square items-center justify-center rounded-md p-1 lg:hover:bg-zinc-300"
                >
                  <i class="fa-solid fa-bars text-xl text-zinc-600"></i>
                </button>
              {/if}
              <h1 class="text-2xl font-bold text-gray-800 dark:text-zinc-100">Sermons</h1>
            </div>
          {/if}

          <Content />
        </div>
        {#if !loadProject}
          <ToastContainer placement="top-left" let:data>
            <FlatToast {data} />
            <!-- Provider template for your toasts -->
          </ToastContainer>
          <!-- <YouTubeProgress></YouTubeProgress> -->
        {/if}
      </div></Modal
    >
  </div>
{/if}

<svelte:head>
  <link rel="stylesheet" href="https://kit.fontawesome.com/4f11e66f04.css" crossorigin="anonymous" />
</svelte:head>

<style global lang="postcss">
  @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap');

  @tailwind base;
  @tailwind components;
  @tailwind utilities;
  html {
    font-family: 'Roboto', sans-serif;
    height: 100%;
    max-height: 100%;
    height: 100svh;
    height: var(--app-height);
  }
  h1,
  h2,
  h3 {
    font-family: 'Montserrat', sans-serif;
  }
  h4,
  h5,
  h6 {
    font-family: 'Roboto', sans-serif;
  }

  #blogcontent li {
    margin-left: 2.5rem !important;
    text-indent: -1.25rem !important; /* Hanging indent */
  }
  #blogcontent {
    ol,
    ul {
      margin-left: 2.5rem !important;
    }
  }

  h1 {
    font-size: 32px;
  }
  h2 {
    @apply text-2xl;
  }
  h3 {
    @apply text-xl;
  }

  /* @media only screen and (max-width: 1024px) {
        html {
            font-size: 40px;
        }

        audio::-webkit-media-controls-play-button {
            transform: scale(3, 3);
            margin: 10px;
        }

        audio::-webkit-media-controls-timeline {
            transform: scale(1, 10);  
        }
    }

    @media only screen and (max-width: 240px) {
        html {
            font-size: 20px;
        }
    } */

  .sidebar {
    z-index: 100;
    height: 100%;
  }

  @keyframes rotating {
    from {
      -ms-transform: rotate(0deg);
      -moz-transform: rotate(0deg);
      -webkit-transform: rotate(0deg);
      -o-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    to {
      -ms-transform: rotate(360deg);
      -moz-transform: rotate(360deg);
      -webkit-transform: rotate(360deg);
      -o-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }

  .rotating {
    -webkit-animation: rotating 2s linear infinite;
    -moz-animation: rotating 2s linear infinite;
    -ms-animation: rotating 2s linear infinite;
    -o-animation: rotating 2s linear infinite;
    animation: rotating 2s linear infinite;
  }

  .element-container .moveable-control-box {
    display: none !important;
  }

  .element-container.show > .moveable-control-box {
    display: block !important;
  }

  .element-container > * {
    pointer-events: all;
  }

  .sermon {
    background: -webkit-linear-gradient(92.82deg, #ef446e 10.42%, #ff8755 79.72%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .sermonbg {
    background: -webkit-linear-gradient(92.82deg, #ef446e 10.42%, #ff8755 79.72%);
  }

  .hoversermonbg:hover {
    background: -webkit-linear-gradient(92.82deg, #ef446e 10.42%, #ff8755 79.72%);
  }

  :root {
    --toastBackground: #125618;
    --toastColor: #ffffff;
  }
  .toast-wrap {
    display: contents;
    font-family: Roboto, sans-serif;
    font-size: 0.875rem;
    overflow: hidden;
    @apply rounded-md;
  }

  .toast-wrap :global(strong) {
    font-weight: 600;
  }

  ._toastMsg {
    font-family: Roboto, sans-serif;
    @apply rounded-md;
  }

  /* start alert style specifically for bottom right placement & light theme */
  .toast-container.bottom-right {
    @apply !w-full !max-w-md;
  }

  .toast-container.bottom-right .st-toast.light {
    @apply flex w-full items-center rounded-md !bg-white px-6 py-5 shadow-md;
  }

  .toast-container.bottom-right .st-toast.light.info {
    border-left: 40px solid #1979eb !important;
  }

  .toast-container.bottom-right .st-toast.light.warning {
    border-left: 40px solid #f5bf35 !important;
  }

  .toast-container.bottom-right .st-toast.light.success {
    border-left: 40px solid #33c4b6 !important;
  }

  .toast-container.bottom-right .st-toast.light.danger {
    border-left: 40px solid #e20f0f !important;
  }

  .toast-container.bottom-right .st-toast.light:before,
  .toast-container.bottom-right .st-toast.light .st-toast-icon {
    @apply hidden;
  }

  .toast-container.bottom-right .st-toast.light .st-toast-details,
  .toast-container.bottom-right .st-toast.light .st-toast-details .st-toast-description {
    @apply m-0;
  }

  .toast-container.bottom-right .st-toast.light .st-toast-details .st-toast-title,
  .toast-container.bottom-right .st-toast.light .st-toast-details .st-toast-description {
    @apply text-sm;
    font-family: 'Open Sans', sans-serif;
  }

  .toast-container.bottom-right .st-toast.light .st-toast-details .st-toast-title {
    @apply font-bold;
  }

  .toast-container.bottom-right .st-toast.light .st-toast-close-btn {
    @apply h-auto min-h-0 w-auto min-w-0;
  }

  /* end alert style specifically for bottom right placement & light theme */

  /* start popup modal style for single actions only */

  .swal2-container .swal2-modal:not(.with-multiple-actions) {
    @apply p-6;
  }

  .swal2-container .swal2-modal:not(.with-multiple-actions) .swal2-icon {
    @apply mx-auto mb-4 mt-0 h-14 w-14 border-warning text-warning;
  }

  .swal2-container .swal2-modal:not(.with-multiple-actions) .swal2-icon .swal2-icon-content {
    @apply text-5xl;
  }

  .swal2-container .swal2-modal:not(.with-multiple-actions) .swal2-title {
    @apply mb-5 p-0 text-lg leading-6 text-gray-900;
  }

  .swal2-container .swal2-modal:not(.with-multiple-actions) .swal2-html-container {
    @apply mx-0 mb-5 mt-0 text-sm text-gray-500;
  }

  .swal2-container .swal2-modal:not(.with-multiple-actions) .swal2-actions {
    @apply m-0;
  }

  .swal2-container .swal2-modal:not(.with-multiple-actions) .swal2-actions .swal2-confirm {
    @apply flex w-full items-center gap-1 rounded-md bg-rose-500 px-3 py-2 font-medium text-zinc-100 hover:bg-rose-600 lg:w-auto;
  }

  .swal2-container .swal2-modal:not(.with-multiple-actions) .swal2-actions .swal2-cancel {
    @apply flex w-full items-center rounded-md border border-2 border-zinc-400 px-3 py-2 font-medium text-white lg:w-auto;
  }

  .swal2-container .swal2-modal:not(.with-multiple-actions) .swal2-actions .swal2-confirm.swal2-styled {
    @apply hover:bg-none;
  }

  /* end popup modal style for single actions only */

  @layer utilities {
    .z-full {
      z-index: 9999;
    }
    .h-nav {
      height: 60px;
    }

    .grid-sizer {
      @apply w-1/4;
    }

    .image-item {
      @apply w-1/4;
    }

    .tippy-box[data-theme~='light'] .tippy-content {
      @apply rounded-lg p-0;
    }
  }

  .spinner {
    border: 0.25rem solid;
    border-radius: 50%;
    border-top: 0.25rem solid;
    animation: spinner 1s linear infinite;
  }

  @keyframes spinner {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .tippy-box {
    font-size: 1em !important;
  }

  .forceWidthMargin {
    width: auto !important;
    margin: auto !important;
    height: 100% !important;
  }

  /* Tippy Custom Themes */
  /* Warning Theme */
  .tippy-box[data-theme~='sswarning'] {
    border: 1px solid #ffa726 !important;
    @apply rounded-lg bg-orange-100 p-2;
  }

  .tippy-box[data-theme~='sswarning'][data-placement^='top'] > .tippy-arrow::before {
    border-top-color: #ffe0b2 !important;
  }
  .tippy-box[data-theme~='sswarning'][data-placement^='bottom'] > .tippy-arrow::before {
    border-bottom-color: #ffa726 !important;
  }
  .tippy-box[data-theme~='sswarning'][data-placement^='left'] > .tippy-arrow::before {
    border-left-color: #ffa726 !important;
  }
  .tippy-box[data-theme~='sswarning'][data-placement^='right'] > .tippy-arrow::before {
    border-right-color: #ffa726 !important;
  }

  /* Info Theme */
  .tippy-box[data-theme~='ssinfo'] {
    border: 1px solid #42a5f5 !important;
    @apply rounded-lg bg-blue-100 p-2;
  }

  .tippy-box[data-theme~='ssinfo'][data-placement^='top'] > .tippy-arrow::before {
    border-top-color: #bbdefb !important;
  }
  .tippy-box[data-theme~='ssinfo'][data-placement^='bottom'] > .tippy-arrow::before {
    border-bottom-color: #42a5f5 !important;
  }
  .tippy-box[data-theme~='ssinfo'][data-placement^='left'] > .tippy-arrow::before {
    border-left-color: #42a5f5 !important;
  }
  .tippy-box[data-theme~='ssinfo'][data-placement^='right'] > .tippy-arrow::before {
    border-right-color: #42a5f5 !important;
  }

  /* Neutral Theme */
  .tippy-box[data-theme~='ssneutral'] {
    border: 1px solid #bdbdbd !important;
    @apply rounded-lg bg-gray-100 p-2;
  }

  .tippy-box[data-theme~='ssneutral'][data-placement^='top'] > .tippy-arrow::before {
    border-top-color: #f5f5f5 !important;
  }
  .tippy-box[data-theme~='ssneutral'][data-placement^='bottom'] > .tippy-arrow::before {
    border-bottom-color: #bdbdbd !important;
  }
  .tippy-box[data-theme~='ssneutral'][data-placement^='left'] > .tippy-arrow::before {
    border-left-color: #bdbdbd !important;
  }
  .tippy-box[data-theme~='ssneutral'][data-placement^='right'] > .tippy-arrow::before {
    border-right-color: #bdbdbd !important;
  }

  /**
    This class is to be used for AI Camera crew purposes.
    Reference components/face-tracker/FaceTracker.svelte
  */
  .ai-camera-crew {
    /* max-width: 100% !important;
    left: 0 !important;

    video {
      width:100%;
      height: auto;
    } */
  }
  /**
  end of AI Camera crew styles
  */

  .btn {
    @apply rounded-lg px-4 py-2 font-semibold transition-colors;
    &:disabled {
      @apply cursor-not-allowed opacity-60;
    }
  }

  .btn-primary {
    @apply bg-sermon-pink text-sermon-base-50;
  }

  .btn-secondary {
    @apply border border-sermon-pink bg-transparent text-sermon-pink;

    &:not(:disabled):hover {
      @apply text-sermon-base-50;
    }
  }

  .btn-tertiary {
    @apply bg-transparent text-sermon-pink;

    &:not(:disabled):hover {
      @apply underline;
    }
  }

  .btn-primary,
  .btn-secondary {
    &:not(:disabled):hover {
      @apply bg-gradient-to-r from-sermon-pink to-sermon-orange;
    }
  }
</style>
